import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Alert from '@mui/material/Alert'
import { styled } from '@mui/material/styles'
import React, { useState } from 'react'
import { type DisbursementAccount } from '../../../../api/schema-dtos/AssetHolderDisbursementDomainDtos'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse, Grid,
  IconButton,
  type IconButtonProps,
  Typography
} from '@mui/material'
import AddDisbursementAccountComponent from './AddDisbursementAccountComponent'
import { type AssetHolderIdProps } from '../../pages/DashboardHome'

interface AccountsComponentProps extends AssetHolderIdProps {
  disbursementAccounts: DisbursementAccount[]
  hasPassedKyc: boolean
  maxCardWidth: number
  multiAccountView: boolean
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props
  return <IconButton {...other} />
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest
  })
}))

const AccountsComponent: React.FC<AccountsComponentProps> = ({
  assetHolderId,
  disbursementAccounts,
  hasPassedKyc,
  maxCardWidth,
  multiAccountView
}: AccountsComponentProps) => {
  const [expanded, setExpanded] = useState(!multiAccountView)

  const handleExpandClick = (): void => {
    setExpanded(!expanded)
  }

  const ACCOUNT_TYPE_BANK = 'Bank account'
  const ACCOUNT_TYPE_BITCOIN = 'Bitcoin wallet address'

  const getDisbursementAccountsDisplay = (disbursementAccounts: DisbursementAccount[]): Array<{
    account: string
    type: string
    bankName: string
    bankAccountNumber: string
    branchCode: string
    accountHolderName: string
    additionalInfo: boolean
    createdAt: string
    verified: boolean
    verifiedAt: string
    verifiedBy: string
    verificationDescription: string
  }> => {
    return disbursementAccounts.map(account => {
      let accountName: string = ''
      let type: string = ''

      let bankName: string = ''
      let bankAccountNumber: string = ''
      let branchCode: string = ''
      let accountHolderName: string = ''

      if (account.accountDetails.accountType === 'BitcoinAddress') {
        accountName = account.accountDetails.bitcoinAddress
        type = ACCOUNT_TYPE_BITCOIN
      }

      if (account.accountDetails.accountType === 'BankAccount') {
        accountName = `${account.accountDetails.bankName} ${account.accountDetails.bankAccountNumber}`
        bankName = account.accountDetails.bankName
        bankAccountNumber = account.accountDetails.bankAccountNumber
        branchCode = account.accountDetails.branchCode
        accountHolderName = account.accountDetails.accountHolderName
        type = ACCOUNT_TYPE_BANK
      }

      const verified = account.verifiedAndActive
      let verifiedAt: string = ''
      let verifiedBy: string = ''

      if (verified) {
        verifiedBy = account.verifiedById === null || (account.verifiedById === assetHolderId) ? 'you' : account.verifiedByEmailAddress ?? ''
        verifiedAt = account.verifiedAtFormatted ?? ''
      }

      return {
        account: accountName,
        type,
        bankName,
        bankAccountNumber,
        branchCode,
        accountHolderName,
        additionalInfo: account.accountDetails.accountType === 'BankAccount',
        createdAt: account.createdAtFormatted,
        verified,
        verifiedAt,
        verifiedBy,
        verificationDescription: account.accountStatusDescription
      }
    })
  }

  return (
    <>
      <Grid container spacing={2}>
        {getDisbursementAccountsDisplay(disbursementAccounts).map((account, index: number) => (
          <Grid item md={12} xs={12} key={index}>

            <Card variant={'outlined'} sx={{ maxWidth: maxCardWidth }}>
              <CardHeader title={`${account.type}`}>
              </CardHeader>

              <CardContent>
                { multiAccountView && !account.verified && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Your cash out account details still need to be verified. Please check your email for your verification link.
                  </Alert>
                )}

                <Typography variant={'h4'} color={'primary'}>{account.account}</Typography>
                <Typography variant={'caption'}>Updated: {account.createdAt}</Typography>

                {multiAccountView && account.verified && account.verifiedAt !== '' && (
                  <Typography variant={'caption'} component={'p'}>Verified at {account.verifiedAt}</Typography>
                )}
                {multiAccountView && account.verified && account.verifiedBy !== '' && (
                  <Typography variant={'caption'} component={'p'}>Verified by {account.verifiedBy}</Typography>
                )}

                {account.additionalInfo && (
                  <>
                    <CardActions disableSpacing sx={{ p: 0 }}>
                      <ExpandMore
                        expand={expanded}
                        onClick={handleExpandClick}
                        aria-expanded={expanded}
                        aria-label="show more" sx={{ p: 0 }}>
                        <ExpandMoreIcon/>
                      </ExpandMore>
                    </CardActions>

                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <CardContent sx={{ p: 0 }}>
                        {account.type === ACCOUNT_TYPE_BANK && (
                          <>
                            <Box display={'flex'} justifyContent={'space-between'}>
                              <Typography variant={'body1'}>Account Holder:</Typography>
                              <Typography variant={'body2'}>{account.accountHolderName}</Typography>
                            </Box>
                            <Box display={'flex'} justifyContent={'space-between'}>
                              <Typography variant={'body1'}>Bank Name:</Typography>
                              <Typography variant={'body2'}>{account.bankName}</Typography>
                            </Box>
                            <Box display={'flex'} justifyContent={'space-between'}>
                              <Typography variant={'body1'}>Branch code:</Typography>
                              <Typography variant={'body2'}>{account.branchCode}</Typography>
                            </Box>
                          </>
                        )}
                      </CardContent>
                    </Collapse>
                  </>
                )}

              </CardContent>
            </Card>
          </Grid>
        ))}

      </Grid>
      { multiAccountView && (
        <Box sx={{ mt: 2, p: 0 }}>
          <AddDisbursementAccountComponent
            assetHolderId={assetHolderId}
            hasPassedKyc={hasPassedKyc}
          />
        </Box>
      )}
    </>

  )
}

export default AccountsComponent
